import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import type { Currency, Lang } from './api';
import { STRINGS } from './i18n';
import type { Strings } from './i18n';
import { localize, splitLang } from './paths';

/**
 * Корзина, язык и валюта.
 *
 * Корзина живёт в браузере, сервер её не хранит. В ней только «что и сколько» —
 * названия и цены не сохраняем намеренно: пролежав неделю, они разойдутся с
 * каталогом, а показать старую цену хуже, чем сходить за свежей.
 */

export interface CartLine {
  variantId: string;
  qty: number;
}

interface Store {
  lang: Lang;
  setLang: (lang: Lang) => void;
  currency: Currency;
  setCurrency: (currency: Currency) => void;
  t: Strings;

  lines: CartLine[];
  count: number;
  add: (variantId: string, qty?: number) => void;
  setQty: (variantId: string, qty: number) => void;
  remove: (variantId: string) => void;
  clear: () => void;
}

const StoreContext = createContext<Store | null>(null);

const CART_KEY = 'vishap.cart';
const LANG_KEY = 'vishap.lang';
const CURRENCY_KEY = 'vishap.currency';

/** Браузер может запретить хранилище — приватный режим, заблокированные куки */
function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Корзина переживёт сессию, но не перезагрузку. Ронять сайт из-за этого нельзя
  }
}

/**
 * Сайт открывается на английском. Язык браузера не угадываем намеренно:
 * магазин в Ереване, а русский браузер у армянина или английский у туриста
 * говорят о языке мало. Выбор в переключателе запоминается: кто выбрал
 * русский, на голом vishap-tea.com в следующий раз попадёт на /ru.
 */
function savedLang(): Lang | null {
  const saved = read<Lang | null>(LANG_KEY, null);
  return saved === 'en' || saved === 'ru' || saved === 'hy' ? saved : null;
}

export function StoreProvider({ children }: { children: ReactNode }) {
  // Язык берём из адреса, а не из памяти: у каждой языковой версии свой адрес
  const location = useLocation();
  const navigate = useNavigate();
  const { lang, rest } = splitLang(location.pathname);
  const [currency, setCurrencyState] = useState<Currency>(() =>
    read<Currency>(CURRENCY_KEY, 'AMD'),
  );
  const [lines, setLines] = useState<CartLine[]>(() => read<CartLine[]>(CART_KEY, []));

  useEffect(() => write(CART_KEY, lines), [lines]);

  // Только при первом заходе и только с английского адреса без приставки.
  // Ссылку на /ru или /hy, которой с вами поделились, не перебиваем
  useEffect(() => {
    const saved = savedLang();
    if (lang === 'en' && saved && saved !== 'en') {
      navigate(localize(rest, saved) + location.search + location.hash, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback(
    (next: Lang) => {
      write(LANG_KEY, next);
      navigate(localize(rest, next) + location.search + location.hash);
    },
    [navigate, rest, location.search, location.hash],
  );

  const setCurrency = useCallback((next: Currency) => {
    setCurrencyState(next);
    write(CURRENCY_KEY, next);
  }, []);

  const add = useCallback((variantId: string, qty = 1) => {
    setLines((prev) => {
      const existing = prev.find((l) => l.variantId === variantId);
      if (existing) {
        return prev.map((l) => (l.variantId === variantId ? { ...l, qty: l.qty + qty } : l));
      }
      return [...prev, { variantId, qty }];
    });
  }, []);

  const setQty = useCallback((variantId: string, qty: number) => {
    setLines((prev) =>
      qty <= 0
        ? prev.filter((l) => l.variantId !== variantId)
        : prev.map((l) => (l.variantId === variantId ? { ...l, qty } : l)),
    );
  }, []);

  const remove = useCallback((variantId: string) => {
    setLines((prev) => prev.filter((l) => l.variantId !== variantId));
  }, []);

  const clear = useCallback(() => setLines([]), []);

  const value = useMemo<Store>(
    () => ({
      lang,
      setLang,
      currency,
      setCurrency,
      t: STRINGS[lang],
      lines,
      count: lines.reduce((sum, l) => sum + l.qty, 0),
      add,
      setQty,
      remove,
      clear,
    }),
    [lang, setLang, currency, setCurrency, lines, add, setQty, remove, clear],
  );

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore(): Store {
  const store = useContext(StoreContext);
  if (!store) throw new Error('useStore вызван вне StoreProvider');
  return store;
}
